import React from 'react'
import classNames from 'classnames';
import Modal from 'components/Modal';
import {
  CHART_TYPE_AREA,
  CHART_TYPE_BAR,
  CHART_TYPE_CARD,
  CHART_TYPE_DOUGHNUT,
  CHART_TYPE_GAUGE,
  CHART_TYPE_LINE,
  CHART_TYPE_PIE,
  CHART_TYPE_TABLE,
  CHART_TYPE_HEATMAP,
  CHART_TYPE_MAP,
  CHART_TYPE_INSIGHTS
} from '../../chartConfigurations'
import { DASHBOARD_ACCENT_COLOR } from '../../../../../../utils/constants';
import AreaIcon from './icons/area'
import BarIcon from './icons/bar'
import CardIcon from './icons/card'
import DoughnutIcon from './icons/doughnut'
import GaugeIcon from './icons/gauge'
import LineIcon from './icons/line'
import PieIcon from './icons/pie'
import TableIcon from './icons/table'
import HeatmapIcon from './icons/heatmap'
import InsightsIcon from './icons/insights'
import { ChartMapIcon } from 'images/common'

const iconMap = {
  [CHART_TYPE_AREA]: <AreaIcon />,
  [CHART_TYPE_BAR]: <BarIcon />,
  [CHART_TYPE_CARD]: <CardIcon />,
  [CHART_TYPE_DOUGHNUT]: <DoughnutIcon />,
  [CHART_TYPE_GAUGE]: <GaugeIcon />,
  [CHART_TYPE_LINE]: <LineIcon />,
  [CHART_TYPE_PIE]: <PieIcon />,
  [CHART_TYPE_TABLE]: <TableIcon />,
  [CHART_TYPE_HEATMAP]: <HeatmapIcon />,
  [CHART_TYPE_MAP]: <ChartMapIcon />,
  [CHART_TYPE_INSIGHTS]: <InsightsIcon/>
}

const ChartIcon = ({ chartType }) => iconMap[chartType] || null

const getTypeGroup = (type) => {
  if ([CHART_TYPE_LINE, CHART_TYPE_AREA, CHART_TYPE_BAR].includes(type)) return 'cartesian'
  if ([CHART_TYPE_PIE, CHART_TYPE_DOUGHNUT, CHART_TYPE_GAUGE].includes(type)) return 'circular'
  if (type === CHART_TYPE_CARD) return 'card'
  if (type === CHART_TYPE_TABLE) return 'table'
  return null
}

const ChartTypeButton = ({
  option = null,
  isActive = false,
  onSelect = (val) => null,
  disabled,
  color = DASHBOARD_ACCENT_COLOR
}) => {

  return (
    <div
      className={classNames(
        'chart-type-select__type-button',
        {
          [`viso-text-${color} viso-bg-${color}-10 viso-border-transparent chart-type-select__type-button--selected`]: isActive,
          'chart-type-select__type-button__inactive': !isActive,
          'chart-type-select__type-button__disabled': disabled
        }
      )}
      title={disabled && "Your user does not have permissions to view insights - contact the workspace administrator"}
      onClick={() => onSelect(option)}
    >
      <div className='chart-type-select__type-button__overlay' />
      <ChartIcon chartType={option.value} />
      <div
        className={classNames("chart-type-select__type-button__label-text", {
          'viso-text-text': isActive
        })}>
        {option.label}
      </div>
    </div>
  )
}

const ConfirmTypeChangeModal = ({ open, onConfirm, onCancel }) => {
  if (!open) return null;

  const buttons = [
    <button className="modal__confirm" key="confirm" onClick={onConfirm}>
      Reset
    </button>,
    <button className="modal__cancel" key="reset" onClick={onCancel}>
      Cancel
    </button>,
  ];

  return (
    <Modal
      buttons={buttons}
      classNames="modal__frame_sm"
      title={'Change Chart Type'}
      body={
        <div>
          Some chart settings may not be compatible with the selected chart type. Incompatible settings will be reset.
        </div>
      }
      onClose={onCancel}
    />
  )
}

const NoPermissionModal = ({ open, onConfirm, onCancel }) => {
  if (!open) return null;

  const buttons = [
    <button className="modal__confirm" key="confirm" onClick={onCancel}>
      Cancel
    </button>,
  ];

  return (
    <Modal
      buttons={buttons}
      classNames="modal__frame_sm"
      title={'No Permission'}
      body={
        <div>
          You do not have permission to view insights. Please contact your workspace administrator.
        </div>
      }
      onClose={onCancel}
    />
  )
}

export const ChartTypeSelect = ({
  label = '',
  selectedOption = {},
  options = [],
  onChange = (val) => null,
  className = '',
  color,
  workspace
}) => {

  const [showConfirm, setShowConfirm] = React.useState(false)
  const [pendingOption, setPendingOption] = React.useState(null)
  const [showNoPermissionModal, setShowNoPermissionModal] = React.useState(false)

  const hasPermissionToAccessAiInsight = workspace?.features?.aiInsights?.enabled;

  const handleSelect = (option) => {
    if (!option || option.value === selectedOption.value) return;

    if(option.value === CHART_TYPE_INSIGHTS && !hasPermissionToAccessAiInsight) {
      setShowNoPermissionModal(true)
      return
    }
    
    const currentGroup = getTypeGroup(selectedOption?.value)
    const nextGroup = getTypeGroup(option.value)
    if (!currentGroup || currentGroup === nextGroup) {
      onChange(option)
      return
    }
    setPendingOption(option)
    setShowConfirm(true)
  }

  const handleConfirm = () => {
    if (pendingOption) {
      onChange(pendingOption)
    }
    setShowConfirm(false)
    setPendingOption(null)
  }

  const handleCancel = () => {
    setShowConfirm(false)
    setPendingOption(null)
  }

  return (
    <div className={className}>
      {label &&
        <div className='chart-editor__label'>
          <div className="text-field__label1">{label}</div>
        </div>
      }
      <div className='chart-type-select__grid-layout'>
        {options.map((option, index) => {
          return (
          <ChartTypeButton
            key={`${option.value}_${index}`}
            color={color}
            option={option}
            disabled={option.value === CHART_TYPE_INSIGHTS && !hasPermissionToAccessAiInsight}
            isActive={selectedOption.value === option.value}
            onSelect={handleSelect}
          />
        )})}
      </div>
      <ConfirmTypeChangeModal open={showConfirm} onConfirm={handleConfirm} onCancel={handleCancel} />
      <NoPermissionModal open={showNoPermissionModal} onCancel={() => setShowNoPermissionModal(false)} />
    </div>
  )
}